import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  GlobeAltIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const GeneralSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [formData, setFormData] = useState({
    gymName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    currency: 'DA',
    language: 'fr',
    timezone: 'Africa/Algiers'
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        gymName: settings.gymName || '',
        address: settings.address || '',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        currency: settings.currency || 'DA',
        language: settings.language || 'fr',
        timezone: settings.timezone || 'Africa/Algiers'
      });
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    onSettingsChange({ ...settings, ...newFormData });
    onHasChanges(true);
  };

  const currencies = [
    { value: 'DA', label: 'Dinar Algérien (DA)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'USD', label: 'Dollar US ($)' }
  ];

  const languages = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'Africa/Algiers', label: 'Alger (UTC+1)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Europe/London', label: 'Londres (UTC+0)' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nom de la salle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <BuildingOfficeIcon className="h-4 w-4" />
            Nom de la salle
          </label>
          <input
            type="text"
            value={formData.gymName}
            onChange={(e) => handleChange('gymName', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nom de votre salle de sport"
          />
        </motion.div>

        {/* Adresse */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <MapPinIcon className="h-4 w-4" />
            Adresse
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Adresse complète"
          />
        </motion.div>

        {/* Téléphone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <PhoneIcon className="h-4 w-4" />
            Téléphone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="+213 XXX XXX XXX"
          />
        </motion.div>

        {/* Email */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <EnvelopeIcon className="h-4 w-4" />
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="contact@gym.dz"
          />
        </motion.div>

        {/* Site web */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <GlobeAltIcon className="h-4 w-4" />
            Site web
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://votre-site.dz"
          />
        </motion.div>

        {/* Devise */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <CurrencyDollarIcon className="h-4 w-4" />
            Devise
          </label>
          <select
            value={formData.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {currencies.map(currency => (
              <option key={currency.value} value={currency.value} className="bg-gray-800 text-white">
                {currency.label}
              </option>
            ))}
          </select>
        </motion.div>

        {/* Langue par défaut */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <LanguageIcon className="h-4 w-4" />
            Langue par défaut
          </label>
          <select
            value={formData.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {languages.map(lang => (
              <option key={lang.value} value={lang.value} className="bg-gray-800 text-white">
                {lang.label}
              </option>
            ))}
          </select>
        </motion.div>

        {/* Fuseau horaire */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <ClockIcon className="h-4 w-4" />
            Fuseau horaire
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value} className="bg-gray-800 text-white">
                {tz.label}
              </option>
            ))}
          </select>
        </motion.div>
      </div>

      {/* Aperçu des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Aperçu</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-white/60">Nom :</span>
            <span className="text-white ml-2">{formData.gymName || 'Non défini'}</span>
          </div>
          <div>
            <span className="text-white/60">Devise :</span>
            <span className="text-white ml-2">{formData.currency}</span>
          </div>
          <div>
            <span className="text-white/60">Langue :</span>
            <span className="text-white ml-2">
              {languages.find(l => l.value === formData.language)?.label}
            </span>
          </div>
          <div>
            <span className="text-white/60">Fuseau horaire :</span>
            <span className="text-white ml-2">
              {timezones.find(t => t.value === formData.timezone)?.label}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GeneralSettings; 